import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Camera, Sparkles, ClipboardCheck, ArrowRight, Images, RotateCcw } from 'lucide-react';
import { useT } from '../studioI18n';
import { useProject, nextStep } from '../ProjectContext';
import { useStudioAuth } from '../StudioAuthContext';
import { fileUrl, studioApi } from '../studioApi';
import { Page, MotionButton, Card, stagger } from '../components/Motion';
import { Stepper } from '../components/StudioHeader';
import { UploadStep } from '../steps/UploadStep';
import { DesignStep } from '../steps/DesignStep';
import { RevealStep } from '../steps/RevealStep';
import { EstimateStep } from '../steps/EstimateStep';

const Loading = () => <div className="min-h-[50dvh] flex items-center justify-center" data-testid="studio-loading"><div className="relative h-14 w-14 rounded-full bg-teal-600/80 shadow-lg st-ring" /></div>;

export default function ProjectPage() {
  const { t } = useT();
  const { customer } = useStudioAuth();
  const { project, variantsByPhoto, reload } = useProject();
  const navigate = useNavigate();
  if (!project) return <Loading />;
  const stage = project.lead.stage;
  const renders = Object.values(variantsByPhoto).reduce((n, v) => n + v.length, 0);
  const next = nextStep(project);
  const stageIdx = ['registered', 'photos', 'simulated', 'estimate_requested'].indexOf(stage);

  return (
    <Page testId="customer-dashboard-container" className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
      <motion.div variants={stagger} initial="initial" animate="animate" className="space-y-8">
        <Card className="p-6 sm:p-8 relative overflow-hidden" strong>
          <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-teal-400/20 blur-3xl" />
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-teal-700">{t.stage[stage]}</p>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 mt-1" data-testid="dashboard-title">{t.hello(customer.name.split(' ')[0])}</h1>
          <p className="text-slate-600 mt-2">{t.dashSub}</p>
          <div className="mt-5 flex items-center gap-2" data-testid="dashboard-stage-bar">
            {[0, 1, 2, 3].map((i) => <motion.span key={i} initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ delay: 0.2 + i * 0.1, duration: 0.5, ease: [0.16, 1, 0.3, 1] }} className={`h-2 flex-1 rounded-full origin-left ${i <= stageIdx ? 'bg-teal-600' : 'bg-slate-200'}`} />)}
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            <MotionButton onClick={() => navigate(`/project/${next}`)} data-testid="dashboard-resume-btn">{stage === 'estimate_requested' ? t.seeResult : t.resume} <ArrowRight className="h-5 w-5" /></MotionButton>
            {project.simulations.length > 0 && stage !== 'estimate_requested' && <MotionButton variant="ghost" onClick={() => navigate('/project/estimate')} data-testid="dashboard-estimate-btn"><ClipboardCheck className="h-5 w-5" /> {t.requestEstimate}</MotionButton>}
            {customer.is_demo && <button type="button" onClick={async () => { await studioApi.post('/demo/reset'); await reload(); }} className="h-13 px-4 text-xs font-bold text-slate-500 hover:text-rose-600 inline-flex items-center gap-1" data-testid="dashboard-demo-reset-btn"><RotateCcw className="h-3.5 w-3.5" /> {t.demoReset}</button>}
          </div>
        </Card>

        <div className="grid sm:grid-cols-3 gap-4">
          <Card className="p-5 cursor-pointer" onClick={() => navigate('/project/photos')} whileHover={{ y: -4 }} data-testid="project-card-0">
            <div className="flex items-center justify-between"><span className="h-10 w-10 rounded-2xl bg-teal-600/10 text-teal-700 flex items-center justify-center"><Camera className="h-5 w-5" /></span><span className="text-2xl font-extrabold text-slate-900 tabular-nums">{project.photos.length}<span className="text-sm text-slate-400">/6</span></span></div>
            <p className="font-bold text-slate-900 mt-3">{t.photosCard}</p>
            <div className="flex -space-x-2 mt-2 h-9">{project.photos.slice(0, 5).map((p) => <img key={p.id} src={fileUrl(p.id, 'thumb')} alt="" className="h-9 w-9 rounded-xl object-cover ring-2 ring-white" />)}</div>
          </Card>
          <Card className="p-5 cursor-pointer" onClick={() => navigate(project.simulations.length ? '/project/reveal' : '/project/design')} whileHover={{ y: -4 }} data-testid="project-card-1">
            <div className="flex items-center justify-between"><span className="h-10 w-10 rounded-2xl bg-teal-600/10 text-teal-700 flex items-center justify-center"><Sparkles className="h-5 w-5" /></span><span className="text-2xl font-extrabold text-slate-900 tabular-nums">{renders}</span></div>
            <p className="font-bold text-slate-900 mt-3">{t.simsCard}</p>
            <p className="text-xs text-slate-500 mt-2 flex items-center gap-1"><Images className="h-3.5 w-3.5" /> {project.simulations.length ? t.seeResult : t.newVariation}</p>
          </Card>
          <Card className="p-5 cursor-pointer" onClick={() => navigate('/project/estimate')} whileHover={{ y: -4 }} data-testid="project-card-2">
            <div className="flex items-center justify-between"><span className="h-10 w-10 rounded-2xl bg-teal-600/10 text-teal-700 flex items-center justify-center"><ClipboardCheck className="h-5 w-5" /></span>{stage === 'estimate_requested' && <span className="h-3 w-3 rounded-full bg-teal-500 st-ring relative" />}</div>
            <p className="font-bold text-slate-900 mt-3">{t.estimateCard}</p>
            <p className="text-xs text-slate-500 mt-2" data-testid="dashboard-estimate-status">{stage !== 'estimate_requested' ? t.estimateNone : project.lead.assigned_to_name ? t.estimateAssigned(project.lead.assigned_to_name) : t.estimatePending}</p>
          </Card>
        </div>
      </motion.div>
    </Page>
  );
}

const STEPS = { photos: UploadStep, design: DesignStep, reveal: RevealStep, estimate: EstimateStep };

export function StepPage() {
  const { step } = useParams();
  const { project } = useProject();
  const Step = STEPS[step] || UploadStep;
  if (!project) return <Loading />;
  const done = [project.photos.length ? 'photos' : null, project.simulations.length ? 'design' : null, project.simulations.length ? 'reveal' : null, project.lead.stage === 'estimate_requested' ? 'estimate' : null].filter(Boolean);
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-6 sm:pt-10 pb-32 sm:pb-36">
      <div className="flex justify-center mb-8"><Stepper current={step} done={done} /></div>
      <AnimatePresence mode="wait"><Step key={step} /></AnimatePresence>
    </div>
  );
}
